import { wccModules } from 'enums/wccModules';
import { DateHelpers } from 'helpers/date';
import { BlobWrapper } from 'interfaces/blobWrapper';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import OpusMediaRecorderManager, { OpusMediaRecording } from 'managers/opus';
import { EffectsContainer } from 'mixins/withEffects';
import './audioRecorder.html';
import './audioRecorder.scss';

const mininumRecordingDuration = 2;

export interface AudioRecorderVMConfig {
    onRecorded?: Action<[BlobWrapper]>
}

@injectable()
export default class AudioRecorderVM {
    private recoder = new OpusMediaRecorderManager();
    private recording = ko.observable<OpusMediaRecording>();

    labels = labels;

    audio = ko.observable<Blob>();
    isRecording = ko.observable(false);
    isFinalizing = ko.observable(false);
    
    recordingDuration: Subscribable<string>
    isStateChangeBlocked: Subscribable<boolean>
    isBusy: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: AudioRecorderVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const recordingDuration = this.recording.pluck(recording => moment(recording.endedOn() ?? settings.now()).diff(recording.startedOn(), 's'));
        this.recordingDuration = recordingDuration.pluck(duration => DateHelpers.formatSeconds(duration), '00:00');
        this.isStateChangeBlocked = recordingDuration.pluck(duration => duration < mininumRecordingDuration, false);

        this.isBusy = ko.pureComputed(() => this.isFinalizing());

        effects.register([
            this.isRecording.subscribe(isRecording => this.onStateChange(isRecording)),
            this.audio.subscribeNotNull(audio => config.onRecorded?.({ blob: audio, name: `audio-recording-${Date.now()}.mp3` })),

            () => this.reset()
        ]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'audio-recorder': true
            }
        });
    }

    toggleRecording = () => {
        if (!this.isStateChangeBlocked())
            this.isRecording.toggle();
    }

    reset = async () => {
        try {
            const activeRecording = this.recording();

            if (activeRecording != undefined) {
                await activeRecording.finalize();
            }
        } finally {
            this.audio(undefined);
            this.recording(undefined);
            this.isRecording(false);
        }
    }

    private async onStateChange(isRecording: boolean) {
        if (isRecording) {
            this.recording(await this.recoder.startRecording());
        } else {
            const recording = this.recording();

            if (recording != undefined) {
                try {
                    this.isFinalizing(true);

                    this.audio(await recording.finalize());                    
                } catch (ex) {
                    this.audio(undefined);
                    system.handleError(ex);
                } finally {
                    this.recording(undefined);
                    this.isFinalizing(false);                    
                }
            }
        }
    }
}